import { BasketItem } from '@df/session/api/basket/basket-item';
import { Nexus } from '@tomandco/nexus-api-types';
import defaults from 'lodash-es/defaults';
import reduce from 'lodash-es/reduce';
import each from 'lodash-es/each';
import { Model } from '@df/catalog/model';
import { Product } from '@df/catalog/product/product';

export interface IStockData extends Nexus.Lib.Catalog.Model.Stock.Data {}

export type TStockBasketTest = boolean | { skip: number[] }; // in skip mode we skip basket items of given ids

/**
 * Stock model representing product stock status information from magento
 */
export class Stock extends Model<IStockData, number> {
    /**
     * Object type id
     * @type {String}
     */
    static override type = 'stock';

    /**
     * Max verbosity
     * @type {String}
     */
    override maxVerbosity = 1;

    /**
     * See stock-resource;
     * using cookies we can fake stock statuses for testing
     */
    fakeStockQty?: number;
    fakeStockBackorderQty?: number;

    /**
     * Set this model data.
     * If current model verbosity is higher data will not be changed.
     * If model is not loaded yet, it will be marked as loaded.
     *
     * NOTE: comparing to parent method we extend data with default stock params
     *
     * @param {any}       model data to set
     * @param {number=3}  model verbosity
     */
    protected override set(data: IStockData, verbosity: number): Stock {
        if (this.fakeStockQty !== undefined) {
            data.qty = this.fakeStockQty;
        }
        if (this.fakeStockBackorderQty !== undefined) {
            data.backorders_qty = this.fakeStockBackorderQty;
        }

        super.set(
            defaults(data, {
                qty: 0,
                isOut: false,
                backorders: false,
                backorders_qty: 0
            }),
            verbosity
        );
        return this;
    }

    /**
     * Max qty getter
     * postModel.qty
     *
     */
    get qty(): number {
        return this.data ? this.data.qty : 0;
    }
    /**
     * Max backorders qty getter
     * IMPORTANT: this is positive value! so how many more below qty we can get
     *
     */
    get backordersQty(): number {
        return this.data && this.data.backorders_qty ? this.data.backorders_qty : 0;
    }

    /**
     * Returns max selable qty
     * @return {number}
     */
    get maxQty(): number {
        return this.getQty(true);
    }

    /**
     * Returns max in-stock qty (skipping backorders)
     * @return {number}
     */
    get maxInQty(): number {
        return this.getQty(false);
    }

    getQty(withBackorders = false, withBasket: TStockBasketTest = false) {
        if (!this.isStockManaged || !this.data) {
            return 10000;
        }
        if (this.data.isOut) {
            return 0;
        }
        const qty = this.qty + (withBackorders && this.data.backorders ? this.backordersQty || 0 : 0);
        if (withBasket) {
            const skip = (typeof withBasket === 'object' && withBasket.skip) || [];
            const items = this.app.customer.basket.items.filter(i => !skip.includes(i.id));

            const basketQty = reduce(
                items,
                (memo: number, item: BasketItem) => {
                    if (item.data.product === this.id) {
                        return memo + item.qty;
                    }
                    if (item.product?.isBundle) {
                        const buyRequest = item.data.request;
                        const bundleOptions = buyRequest?.bundle_options || this.getBundleOptionsForStockCheck(item.product);
                        if (bundleOptions) {
                            each(item.product.getBundleSelectionObject(bundleOptions).getSelectedProducts(), selectionProduct => {
                                if (selectionProduct.product && selectionProduct.product.id === this.id) {
                                    memo += selectionProduct.qty;
                                }
                            });
                        }
                    }
                    return memo;
                },
                0
            );
            return Math.max(0, qty - basketQty);
        }
        return qty;
    }

    private getBundleOptionsForStockCheck(product: Product) {
        const bundleOptions = {};
        const options = product.bundleOptions || [];
        options.forEach(o => {
            if (o.required) {
                const defaultSelection = o.defaultSelection;
                const children = o.children || [];
                const selectedChild =
                    (defaultSelection ? children.find(c => c.selection_id === defaultSelection) : children[0]) || children[0];
                if (selectedChild) {
                    bundleOptions[o.id] = {
                        [selectedChild.selection_id]: selectedChild.qty || 1
                    };
                }
            }
        });
        return Object.keys(bundleOptions).length > 0 ? bundleOptions : undefined;
    }

    /**
     * Checks stock status for give qty
     * returns:
     * 0 - out of stock
     * 1 - in stock
     * -1 - backorders only
     */
    checkStockStatus(qty): number {
        if (this.maxQty < qty) {
            return 0;
        }
        if (this.maxInQty < qty) {
            return -1;
        }
        return 1;
    }

    /**
     * Checks if product is out of stock
     *
     * Any item that cannot be bough even with qty 1
     * That DOES include qty checking now (including basket contents)
     *
     * This feature is usually used on listing
     * -------------------------------
     *
     * usage:
     *
     * postModel.isOutOfStock
     *
     */
    get isOutOfStock(): boolean {
        return !!(this.isStockManaged && this.data && this.data.isOut);
    }

    get isTempOutOfStock(): boolean {
        return !!(this.isStockManaged && !this.isOutOfStock && this.data?.isOutTemp && this.maxQty <= 0);
    }

    private get isStockManaged() {
        return !!this.data && !this.data.not_manage;
    }
}
